import React from 'react';
import { useTranslation } from 'react-i18next';
import { RotateCcw } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';

export interface QuotaFilters {
  serviceDesk: string;
  requestType: string;
  sector: string;
  brand: string;
}

export const EMPTY_QUOTA_FILTERS: QuotaFilters = {
  serviceDesk: 'all',
  requestType: 'all',
  sector: 'all',
  brand: 'all',
};

/** Value is what the filter matches on; label is what the row shows. They differ for ids. */
interface FilterOption {
  value: string;
  label: string;
}

interface QuotaFiltersBarProps {
  filters: QuotaFilters;
  onChange: (next: Partial<QuotaFilters>) => void;
  onReset: () => void;
  options: {
    serviceDesks: FilterOption[];
    requestTypes: FilterOption[];
    sectors: FilterOption[];
    brands: FilterOption[];
  };
}

const FilterSelect = ({
  value,
  onValueChange,
  anyLabel,
  options,
}: {
  value: string;
  onValueChange: (value: string) => void;
  anyLabel: string;
  options: FilterOption[];
}) => (
  <Select value={value} onValueChange={onValueChange}>
    {/* Sharing the row evenly, with a floor so a long request type never crushes the others. */}
    <SelectTrigger className="h-[38px] min-w-[170px] flex-1 rounded-[9px]">
      <SelectValue placeholder={anyLabel} />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="all">{anyLabel}</SelectItem>
      {options.map((option) => (
        <SelectItem key={option.value} value={option.value}>
          {option.label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

/**
 * Four filters, one row. No disclosure: the tickets bar folds because it hides eight advanced
 * filters, and a fold that hides two only costs a click.
 */
export const QuotaFiltersBar = ({ filters, onChange, onReset, options }: QuotaFiltersBarProps) => {
  const { t } = useTranslation();
  const showReset = Object.values(filters).some((value) => value !== 'all');

  return (
    <div className="rounded-md bg-gray-50 p-4 dark:bg-zinc-900">
      <div className="flex flex-wrap items-center gap-[10px]">
        <FilterSelect
          value={filters.serviceDesk}
          onValueChange={(value) => onChange({ serviceDesk: value })}
          anyLabel={t('All service desks')}
          options={options.serviceDesks}
        />
        <FilterSelect
          value={filters.requestType}
          onValueChange={(value) => onChange({ requestType: value })}
          anyLabel={t('All request types')}
          options={options.requestTypes}
        />
        <FilterSelect
          value={filters.sector}
          onValueChange={(value) => onChange({ sector: value })}
          anyLabel={t('All sectors')}
          options={options.sectors}
        />
        <FilterSelect
          value={filters.brand}
          onValueChange={(value) => onChange({ brand: value })}
          anyLabel={t('All brands')}
          options={options.brands}
        />

        {/* Nothing set, nothing to reset: an always-on button offers to undo an empty state. */}
        {showReset && (
          <button
            type="button"
            onClick={onReset}
            className="inline-flex h-[38px] items-center gap-[7px] rounded-[9px] border-none bg-transparent px-[13px] text-[13px] font-medium text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <RotateCcw className="h-[15px] w-[15px]" aria-hidden="true" />
            {t('Reset')}
          </button>
        )}
      </div>
    </div>
  );
};
